import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Page, PageHeader, StatusBadge, Modal, Field, inputCls, PrimaryButton, EmptyState,
} from "@/components/lims";
import { Plus, Buildings, UsersThree, Prohibit, ArrowCounterClockwise } from "@phosphor-icons/react";
import { roleLabel, rolesForUser } from "@/lib/roles";

const ROLES = [
  "system_admin", "sample_manager", "analyst", "supervisor", "qa", "client_user",
];

const emptyOrg = { name: "", org_code: "", description: "", portal_label: "", brand_color: "#0F172A", default_tat_days: 10 };
const emptyMember = { user_id: "", role: "analyst", client_id: "" };
const emptyBootstrap = { email: "", name: "", role: "system_admin", client_id: "" };

/**
 * Platform administration of organizations (FR-19 foundation).
 *
 * This screen is deliberately separate from Clients: a client is a customer
 * record inside one laboratory, while an organization is the security tenant
 * itself. Hiding this page from other users is presentation only — the backend
 * is the authorization boundary.
 */
export default function Organizations() {
  const { isPlatformAdmin } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyOrg);
  const [saving, setSaving] = useState(false);
  const [configOrg, setConfigOrg] = useState(null);
  const [config, setConfig] = useState({ portal_label: "", brand_color: "#0F172A", default_tat_days: 10 });

  const [memberOrg, setMemberOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [memberForm, setMemberForm] = useState(emptyMember);
  const [bootstrapOrg, setBootstrapOrg] = useState(null);
  const [bootstrapForm, setBootstrapForm] = useState(emptyBootstrap);
  const [bootstrapResult, setBootstrapResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/organizations");
      setOrgs(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isPlatformAdmin) load(); else setLoading(false); }, [isPlatformAdmin]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/organizations", { ...form, org_code: form.org_code.toUpperCase() });
      toast.success("Organization created");
      setOpen(false);
      setForm(emptyOrg);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  // Deactivation, not deletion: organization records and their history are
  // retained (AC-10).
  const setStatus = async (org, status) => {
    try {
      await api.patch(`/organizations/${org.id}/status`, { status });
      toast.success(status === "Active" ? "Organization reactivated" : "Organization deactivated");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const openConfig = (org) => {
    setConfigOrg(org);
    setConfig({ portal_label: org.portal_label || org.name, brand_color: org.brand_color || "#0F172A", default_tat_days: org.default_tat_days || 10 });
  };

  const saveConfig = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/organizations/${configOrg.id}/portal-config`, { ...config, default_tat_days: Number(config.default_tat_days) });
      toast.success("Portal configuration updated");
      setConfigOrg(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const openMembers = async (org) => {
    setMemberOrg(org);
    setMemberForm(emptyMember);
    const { data } = await api.get(`/organizations/${org.id}/members`);
    setMembers(data);
  };

  const addMember = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/organizations/${memberOrg.id}/members`, memberForm);
      toast.success("Membership granted");
      openMembers(memberOrg);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const revoke = async (userId) => {
    try {
      await api.patch(`/organizations/${memberOrg.id}/members/${userId}/revoke`);
      toast.success("Membership revoked");
      openMembers(memberOrg);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const openBootstrap = (org) => {
    setBootstrapOrg(org);
    setBootstrapForm(emptyBootstrap);
    setBootstrapResult(null);
  };

  const createBootstrapUser = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(
        `/organizations/${bootstrapOrg.id}/users`,
        { ...bootstrapForm, client_id: bootstrapForm.client_id || null },
      );
      setBootstrapResult(data);
      toast.success(data.existing_account
        ? "Existing account added to organization"
        : "Initial organization user created");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const invitationUrl = bootstrapResult?.invitation_token
    ? `${window.location.origin}/accept-invitation?token=${bootstrapResult.invitation_token}`
    : null;

  if (!isPlatformAdmin) {
    return (
      <Page>
        <PageHeader title="Organizations" subtitle="Platform administration" />
        <EmptyState
          icon={Buildings}
          title="Platform administrator access required"
          subtitle="Organization onboarding is restricted to platform administrators."
        />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        title="Organizations"
        subtitle="Laboratory tenants. Each has its own isolated data."
        action={
          <PrimaryButton data-testid="new-org-button" onClick={() => setOpen(true)}>
            <Plus size={18} /> New Organization
          </PrimaryButton>
        }
      />

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        {loading ? (
          <div className="p-8 space-y-3">
            {[...Array(3)].map((_, i) => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}
          </div>
        ) : orgs.length === 0 ? (
          <EmptyState icon={Buildings} title="No organizations yet"
            subtitle="Create one to onboard a laboratory" />
        ) : (
          <table className="w-full text-sm" data-testid="org-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3">Code</th>
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Portal</th>
                <th className="text-left px-5 py-3">Created</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {orgs.map((o) => (
                <tr key={o.id} className="hover:bg-slate-50">
                  <td className="px-5 py-3 font-mono text-xs text-slate-900">{o.org_code}</td>
                  <td className="px-5 py-3 text-slate-900">{o.name}</td>
                  <td className="px-5 py-3"><StatusBadge status={o.status} /></td>
                  <td className="px-5 py-3 text-xs"><span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: o.brand_color || "#0F172A" }} />{o.portal_label || o.name} · {o.default_tat_days || 10}d</td>
                  <td className="px-5 py-3 text-slate-500">{(o.created_at || "").slice(0, 10)}</td>
                  <td className="px-5 py-3 text-right space-x-2 whitespace-nowrap">
                    <button
                      onClick={() => openMembers(o)}
                      className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900"
                    >
                      <UsersThree size={16} /> Members
                    </button>
                    <button
                      data-testid={`bootstrap-user-${o.org_code}`}
                      onClick={() => openBootstrap(o)}
                      className="text-slate-500 hover:text-slate-900"
                    >
                      Create first user
                    </button>
                    <button onClick={() => openConfig(o)} className="text-slate-500 hover:text-slate-900">Configure</button>
                    {o.status === "Active" ? (
                      <button
                        onClick={() => setStatus(o, "Inactive")}
                        className="inline-flex items-center gap-1 text-slate-500 hover:text-status-warn"
                        title="Deactivate. Records are retained."
                      >
                        <Prohibit size={16} /> Deactivate
                      </button>
                    ) : (
                      <button
                        onClick={() => setStatus(o, "Active")}
                        className="inline-flex items-center gap-1 text-slate-500 hover:text-status-ok"
                      >
                        <ArrowCounterClockwise size={16} /> Reactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="mt-3 text-xs text-slate-400">
        Organizations are never deleted. Deactivating one immediately blocks access for
        its members while retaining all records and audit history.
      </p>

      <Modal open={open} onClose={() => setOpen(false)} title="New Organization" testid="org-modal">
        <form onSubmit={submit} className="space-y-4">
          <Field label="Legal name">
            <input className={inputCls} required value={form.name} onChange={set("name")}
              data-testid="org-name-input" />
          </Field>
          <Field label="Organization code">
            <input className={`${inputCls} font-mono uppercase`} required minLength={2} maxLength={16}
              pattern="[A-Za-z0-9][A-Za-z0-9-]{1,15}" value={form.org_code} onChange={set("org_code")}
              placeholder="LAB-002" data-testid="org-code-input" />
            <p className="mt-1 text-xs text-slate-400">
              2–16 characters, letters, digits and hyphens. Permanent once created.
            </p>
          </Field>
          <Field label="Description">
            <input className={inputCls} value={form.description} onChange={set("description")} />
          </Field>
          <Field label="Portal label"><input className={inputCls} value={form.portal_label} onChange={set("portal_label")} placeholder={form.name || "Laboratory name"} /></Field>
          <div className="grid grid-cols-2 gap-4"><Field label="Brand color"><input type="color" className={`${inputCls} h-10`} value={form.brand_color} onChange={set("brand_color")} /></Field><Field label="Default TAT days"><input type="number" min="1" max="365" className={inputCls} value={form.default_tat_days} onChange={set("default_tat_days")} /></Field></div>
          <PrimaryButton type="submit" disabled={saving} className="w-full justify-center">
            {saving ? "Creating…" : "Create organization"}
          </PrimaryButton>
        </form>
      </Modal>

      <Modal open={!!configOrg} onClose={() => setConfigOrg(null)} title={`Portal configuration: ${configOrg?.org_code || ""}`}>
        <form onSubmit={saveConfig} className="space-y-4">
          <Field label="Portal label"><input required className={inputCls} value={config.portal_label} onChange={(e) => setConfig({ ...config, portal_label: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-4"><Field label="Brand color"><input type="color" className={`${inputCls} h-10`} value={config.brand_color} onChange={(e) => setConfig({ ...config, brand_color: e.target.value })} /></Field><Field label="Default TAT days"><input type="number" min="1" max="365" required className={inputCls} value={config.default_tat_days} onChange={(e) => setConfig({ ...config, default_tat_days: e.target.value })} /></Field></div>
          <PrimaryButton type="submit" className="w-full justify-center">Save configuration</PrimaryButton>
        </form>
      </Modal>

      <Modal open={!!memberOrg} onClose={() => setMemberOrg(null)}
        title={`Members: ${memberOrg?.org_code || ""}`} testid="members-modal">
        <div className="space-y-4">
          <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-56 overflow-y-auto">
            {members.length === 0 ? (
              <div className="p-4 text-sm text-slate-400">No members yet.</div>
            ) : members.map((m) => (
              <div key={m.user_id} className="flex items-center justify-between px-3 py-2 text-sm">
                <div className="min-w-0">
                  <div className="text-slate-900 truncate">{m.name || m.user_id}</div>
                  <div className="text-xs text-slate-400 truncate">{m.email}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="max-w-52 text-right text-xs text-slate-500">
                    {rolesForUser(m).map(roleLabel).join(", ")}
                  </span>
                  {m.active ? (
                    <button onClick={() => revoke(m.user_id)}
                      className="text-xs text-slate-400 hover:text-status-critical">Revoke</button>
                  ) : (
                    <span className="text-xs text-slate-300">revoked</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={addMember} className="space-y-3">
            <Field label="User ID">
              <input className={`${inputCls} font-mono text-xs`} required value={memberForm.user_id}
                onChange={(e) => setMemberForm({ ...memberForm, user_id: e.target.value })}
                placeholder="UUID of an existing account" />
            </Field>
            <Field label="Role in this organization">
              <select className={inputCls} value={memberForm.role}
                onChange={(e) => setMemberForm({ ...memberForm, role: e.target.value, client_id: e.target.value === "client_user" ? memberForm.client_id : "" })}>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            {memberForm.role === "client_user" && <Field label="Client ID">
              <input className={`${inputCls} font-mono text-xs`} required value={memberForm.client_id}
                onChange={(e) => setMemberForm({ ...memberForm, client_id: e.target.value })}
                placeholder="CRM client UUID in this laboratory" />
            </Field>}
            <PrimaryButton type="submit" className="w-full justify-center">Grant membership</PrimaryButton>
          </form>
        </div>
      </Modal>

      <Modal
        open={!!bootstrapOrg}
        onClose={() => { setBootstrapOrg(null); setBootstrapResult(null); }}
        title={`Initial user: ${bootstrapOrg?.org_code || ""}`}
        testid="bootstrap-user-modal"
      >
        {bootstrapResult ? (
          <div className="space-y-4">
            {invitationUrl ? (
              <>
                <p className="text-sm text-slate-600">
                  Send this one-time activation link to the user through an approved
                  channel. It expires in {bootstrapResult.invitation_expires_in_hours} hours;
                  the administrator never chooses the user's password or authenticator.
                </p>
                <code
                  data-testid="bootstrap-invitation-url"
                  className="block break-all rounded-md bg-slate-50 border border-slate-200 p-2.5 text-xs font-mono text-slate-700"
                >
                  {invitationUrl}
                </code>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(invitationUrl);
                    toast.success("Link copied");
                  }}
                  className="text-xs text-slate-500 hover:text-slate-900"
                >
                  Copy activation link
                </button>
              </>
            ) : bootstrapResult?.invitation_delivery === "email" ? (
              <p className="text-sm text-slate-600">
                A one-time activation link was e-mailed to the user. It expires in{" "}
                {bootstrapResult.invitation_expires_in_hours} hours; the administrator
                never chooses the user's password or authenticator.
              </p>
            ) : (
              <p className="text-sm text-slate-600">
                This existing account now belongs to the organization and can use its
                existing credentials and mandatory MFA.
              </p>
            )}
            <PrimaryButton
              type="button"
              className="w-full justify-center"
              onClick={() => { setBootstrapOrg(null); setBootstrapResult(null); }}
            >
              Done
            </PrimaryButton>
          </div>
        ) : (
          <form onSubmit={createBootstrapUser} className="space-y-4">
            <p className="text-sm text-slate-600">
              Create the first administrator after onboarding a laboratory. That user
              can then configure CRM clients and invite client-portal members from Team.
            </p>
            <Field label="Full name">
              <input
                data-testid="bootstrap-name"
                className={inputCls}
                required
                value={bootstrapForm.name}
                onChange={(e) => setBootstrapForm({ ...bootstrapForm, name: e.target.value })}
              />
            </Field>
            <Field label="Email">
              <input
                data-testid="bootstrap-email"
                className={inputCls}
                type="email"
                required
                value={bootstrapForm.email}
                onChange={(e) => setBootstrapForm({ ...bootstrapForm, email: e.target.value })}
              />
            </Field>
            <Field label="Persona">
              <select
                data-testid="bootstrap-role"
                className={inputCls}
                value={bootstrapForm.role}
                onChange={(e) => setBootstrapForm({
                  ...bootstrapForm,
                  role: e.target.value,
                  client_id: e.target.value === "client_user" ? bootstrapForm.client_id : "",
                })}
              >
                {ROLES.map((role) => <option key={role} value={role}>{role}</option>)}
              </select>
            </Field>
            {bootstrapForm.role === "client_user" && (
              <Field label="Client ID">
                <input
                  data-testid="bootstrap-client-id"
                  className={`${inputCls} font-mono text-xs`}
                  required
                  value={bootstrapForm.client_id}
                  onChange={(e) => setBootstrapForm({ ...bootstrapForm, client_id: e.target.value })}
                  placeholder="CRM client UUID in this organization"
                />
              </Field>
            )}
            <PrimaryButton type="submit" className="w-full justify-center">
              Create user and activation link
            </PrimaryButton>
          </form>
        )}
      </Modal>
    </Page>
  );
}
